import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface Props {
  total: number;
  porPagina: number;
  cargando?: boolean;
}

/**
 * Paginación de un listado, con la página en la URL.
 *
 * Vive en la dirección y no en el estado del componente para que una página
 * concreta se pueda compartir, el botón «atrás» funcione y volver de un detalle
 * no devuelva a la primera página. Devuelve `null` cuando todo cabe en una: unos
 * controles que no llevan a ninguna parte son ruido.
 */
export function Paginacion({ total, porPagina, cargando = false }: Props) {
  const { t } = useTranslation();
  const [parametros, setParametros] = useSearchParams();

  const paginaActual = Math.max(1, Number(parametros.get('page') ?? 1));
  const paginas = Math.max(1, Math.ceil(total / porPagina));

  const irA = useCallback(
    (numero: number) => {
      const siguientes = new URLSearchParams(parametros);
      if (numero <= 1) siguientes.delete('page');
      else siguientes.set('page', String(numero));
      setParametros(siguientes);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [parametros, setParametros],
  );

  if (total <= porPagina) return null;

  const desde = (paginaActual - 1) * porPagina + 1;
  const hasta = Math.min(paginaActual * porPagina, total);

  return (
    <nav className="paginacion" aria-label={t('documents.pagination')}>
      <span className="paginacion__rango mono">
        {t('documents.showingRange', { desde, hasta, total })}
      </span>
      <div className="paginacion__botones">
        <button
          className="boton boton--discreto"
          type="button"
          disabled={paginaActual <= 1 || cargando}
          onClick={() => irA(paginaActual - 1)}
        >
          {t('viewer.previous')}
        </button>
        {/* Selector, no un rótulo: con 261 personas a 25 por página son once
            páginas, y llegar a la novena pulsando «siguiente» ocho veces es
            trabajo que la máquina puede ahorrar. Sigue siendo un `select`
            nativo, que en el teléfono abre la rueda del sistema y se maneja con
            el teclado sin que nadie programe nada. */}
        <label className="paginacion__pagina">
          <span className="visualmente-oculto">{t('documents.goToPage')}</span>
          <select
            className="paginacion__selector mono"
            value={paginaActual}
            disabled={cargando}
            onChange={(e) => irA(Number(e.target.value))}
          >
            {Array.from({ length: paginas }, (_, i) => i + 1).map((numero) => (
              <option key={numero} value={numero}>
                {t('documents.pageOf', { page: numero, total: paginas })}
              </option>
            ))}
          </select>
        </label>
        <button
          className="boton boton--discreto"
          type="button"
          disabled={paginaActual >= paginas || cargando}
          onClick={() => irA(paginaActual + 1)}
        >
          {t('viewer.next')}
        </button>
      </div>
    </nav>
  );
}
